import React, { useState } from 'react'
import { string, oneOf, func, node, object } from 'prop-types'
import styled from 'styled-components'
import media from 'styled-media-query'

import Link from './Link'
import Label from './Label'

import {
  suggestiveColor,
  primaryTextColor,
  headersColor,
  confirmationColor,
  highlightColor,
  errorColor,
} from './colors'
import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons'

const StyledContainer = styled.div`
  width: 100%;
  flex-direction: column;
`

const StyledFloatingLabelContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
  background-color: white;
  padding-left: 10px;
  padding-right: 10px;
  border-radius: 10px;
  height: 55px;
  width: 100%;
  transition: border 300ms ease-out;
  border: ${({ active, hasError }) =>
    `1px solid ${
      // eslint-disable-next-line no-nested-ternary
      hasError ? errorColor : active ? confirmationColor : suggestiveColor
    }`};

  ${media.greaterThan('medium')`
    height: 65px;
  `}
`

const FloatingLabel = styled.label`
  padding: 0;
  margin: 0;
  border: 0;
  position: absolute;
  color: ${primaryTextColor};
  font-size: 18px !important;
  bottom: 0;
  transition: all 0.2s ease-in-out;
  transform-origin: left top;
  cursor: text;
  pointer-events: none;
  transform: ${({ active }) =>
    active ? 'translate3d(0, -9%, 0) scale(0.65)' : 'none'};
  font-weight: ${({ active }) => (active ? 'bold' : 'regular')};
  line-height: 55px;

  ${media.greaterThan('medium')`
    line-height: 65px;
    font-size: 22px !important;
  `}
`

const StyledInput = styled.input`
  padding: 0;
  margin: 0;
  border: none;
  outline: none;
  height: 50px;
  color: ${headersColor};
  padding-left: 2px;
  font-size: 18px;
`

const StyledRightComponent = styled.div`
  position: absolute;
  right: 25px;
  display: flex;
  align-items: center;
  height: 60px;

  ${media.greaterThan('medium')`
    line-height: 65px;
  `}
`

const Input = ({
  label,
  name,
  value,
  error,
  htmlType,
  onChange,
  onBlur,
  style,
  success,
  RightComponent,
  labelActive = false,
}) => {
  const [active, setActive] = useState(value !== '')
  const [showPassword, setShowPassword] = useState(false)

  const handleFocus = () => {
    setActive(true)
  }

  const handleBlur = (event) => {
    setActive(event.target.value.length !== 0)
  }

  const hasError = Boolean(error)
  const hasSuccess = Boolean(success)
  const isPassword = htmlType === 'password'
  return (
    <StyledContainer style={style} className="empty">
      <StyledFloatingLabelContainer active={active} hasError={hasError}>
        <FloatingLabel active={labelActive || active}>{label}</FloatingLabel>
        <StyledInput
          name={name}
          type={showPassword ? 'text' : htmlType}
          onFocus={handleFocus}
          onBlur={(event) => {
            handleBlur(event)
            onBlur && onBlur(event)
          }}
          value={value}
          onChange={onChange}
        />
        {isPassword && (
          <StyledRightComponent onClick={() => setShowPassword(!showPassword)}>
            <Link
              label={showPassword ? <EyeOutlined /> : <EyeInvisibleOutlined />}
              size={18}
              color={showPassword ? highlightColor : primaryTextColor}
            />
          </StyledRightComponent>
        )}
        {Boolean(RightComponent) && (
          <StyledRightComponent>{RightComponent}</StyledRightComponent>
        )}
      </StyledFloatingLabelContainer>
      {hasError && (
        <Label value={error} type="error" style={{ marginLeft: 2 }} />
      )}
      {hasSuccess && (
        <Label value={success} style={{ marginLeft: 2, color: 'green' }} />
      )}
    </StyledContainer>
  )
}

Input.propTypes = {
  label: string.isRequired,
  // name: string.isRequired,
  error: string,
  value: string,
  htmlType: oneOf(['text', 'password']),
  onChange: func,
  onBlur: func,
  style: object,
  RightComponent: node,
}

export default Input
